// ============================================================================
// LA PASTILLE — rendu DOM du dessin porté par `ui-tokens/badge`.
//
// ⚠️ AUCUN NOMBRE ICI : hauteurs, graisses, tons et rayons viennent tous des jetons. Une valeur écrite
// dans ce fichier serait une 26ᵉ géométrie, c'est-à-dire le problème qu'on vient de résoudre.
//
// ⚠️ CE N'EST PAS UN CONTRÔLE : un `<span>`, pas un bouton, pas de curseur main. Une information qui a
// l'air cliquable se fait cliquer, et ne répond pas. Le seul enfant interactif possible est la croix
// de retrait — et c'est alors un vrai `<button>` DANS le span, pas le span lui-même.
// ============================================================================
import type { CSSProperties, ReactNode } from 'react';
import {
  BADGE_CODE, BADGE_COUNT, BADGE_DOT, BADGE_REMOVE, BADGE_SIZES, BADGE_TONES, BADGE_WEIGHT,
  type BadgeRadius, type BadgeSize, type BadgeTone,
} from '@orgazone/ui-tokens';

export interface BadgeProps {
  children: ReactNode;
  /** Défaut `neutral`. Le ton est un RÔLE, jamais une couleur choisie par l'appelant. */
  tone?: BadgeTone;
  /** `xs` compteur/surimpression · `sm` cellule et ligne de texte (défaut) · `md` en-tête de fiche. */
  size?: BadgeSize;
  /** `code` = mono capitales, pour un identifiant technique — jamais pour un mot du métier. */
  form?: 'text' | 'code';
  /** Point de tête, à l'encre du ton : lisible même quand le fond est pâle. */
  dot?: boolean;
  /** Icône de tête, DIMENSIONNÉE PAR LE CRAN — l'appelant ne choisit pas sa taille. */
  icon?: ReactNode;
  /** Capsule retirable (filtre appliqué). `removeLabel` devient l'`aria-label` du bouton. */
  onRemove?: () => void;
  removeLabel?: string;
  /** Anneau interne : détache la pastille d'un fond de même famille (rangée sélectionnée). */
  ring?: boolean;
  /** Bordure pleine — la seule capsule bordée du dépôt (badge de plateforme du store). */
  border?: boolean;
  /** Défaut `pill`. `ctrl` pour les pastilles denses en cellule de tableau. */
  radius?: BadgeRadius;
  /**
   * COURT-CIRCUITE le thème.
   * ⚠️ Réservé au chrome (`--chrome-*`) : une pastille de bandeau de contexte ne doit PAS suivre la
   * palette de l'application, sinon elle se fond dans l'écran qu'elle est censée interrompre.
   */
  colors?: { bg: string; fg: string };
  title?: string;
  /** Libellé pour lecteur d'écran quand le contenu visible ne suffit pas (« 3 » → « 3 alertes »). */
  srLabel?: string;
  style?: CSSProperties;
}

/** Une valeur de ton → sa variable CSS. `transparent` reste tel quel (ton `muted`). */
function couleur(role: string): string {
  return role === 'transparent' ? 'transparent' : `var(--${role})`;
}

export function Badge({
  children, tone = 'neutral', size = 'sm', form = 'text', dot, icon,
  onRemove, removeLabel, ring, border, radius = 'pill', colors, title, srLabel, style,
}: BadgeProps) {
  const cran = BADGE_SIZES[size];
  const role = BADGE_TONES[tone];
  const bg = colors?.bg ?? couleur(role.bg);
  const fg = colors?.fg ?? couleur(role.fg);
  const code = form === 'code';

  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: cran.gap,
        height: cran.height,
        // Retirable : la croix comble la droite, le padding devient asymétrique.
        padding: onRemove ? `0 ${BADGE_REMOVE.padRight}px 0 ${BADGE_REMOVE.padLeft}px` : `0 ${cran.padH}px`,
        borderRadius: `var(--r-${radius})`,
        background: bg,
        color: fg,
        fontSize: code ? BADGE_CODE.fontSize : cran.fontSize,
        fontWeight: code ? BADGE_CODE.weight : BADGE_WEIGHT,
        fontFamily: code ? 'var(--font-mono)' : undefined,
        textTransform: code ? 'uppercase' : undefined,
        letterSpacing: code ? BADGE_CODE.letterSpacing : undefined,
        // ⚠️ ELLE PEUT RÉTRÉCIR (2026-08-16) : `nowrap` rend `min-content = max-content`, donc la
        // capsule refusait de céder un seul pixel et peignait par-dessus la colonne voisine. On la
        // tronque, on ne l'enroule pas — une pastille sur deux lignes n'est plus une capsule.
        whiteSpace: 'nowrap', maxWidth: '100%', minWidth: 0, overflow: 'hidden',
        border: border ? `1px solid ${fg}` : undefined,
        boxShadow: ring ? `inset 0 0 0 1px ${bg}` : undefined,
        ...style,
      }}
    >
      {dot && <Point color={fg} />}
      {icon && <span style={{ display: 'inline-flex', width: cran.icon, height: cran.icon, flexShrink: 0 }}>{icon}</span>}
      {children}
      {srLabel && <span className="sr-only">{srLabel}</span>}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ?? 'Retirer'}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: BADGE_REMOVE.hit, height: BADGE_REMOVE.hit, marginLeft: 1, flexShrink: 0,
            border: 'none', background: 'transparent', color: 'inherit',
            borderRadius: 'var(--r-pill)', cursor: 'pointer', padding: 0, lineHeight: 1,
          }}
        >
          <svg width={BADGE_REMOVE.icon} height={BADGE_REMOVE.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" aria-hidden>
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  );
}

/**
 * Le point d'état seul — sans capsule.
 * ⚠️ `srLabel` OBLIGATOIRE : un point coloré ne se lit pas au lecteur d'écran. Sans libellé, l'état
 * n'existe que pour qui voit la couleur.
 */
export function StatusDot({ tone, halo, srLabel }: { tone: BadgeTone; halo?: boolean; srLabel: string }) {
  const fg = couleur(BADGE_TONES[tone].fg);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Point color={fg} halo={halo} />
      <span className="sr-only">{srLabel}</span>
    </span>
  );
}

function Point({ color, halo }: { color: string; halo?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: BADGE_DOT.size, height: BADGE_DOT.size, borderRadius: 'var(--r-pill)',
        background: color, flexShrink: 0,
        boxShadow: halo ? `0 0 0 ${BADGE_DOT.halo}px color-mix(in oklch, ${color} ${BADGE_DOT.haloMix}%, transparent)` : undefined,
      }}
    />
  );
}

/**
 * Le COMPTEUR — sur un onglet, ou en surimpression d'un déclencheur (cloche).
 *
 * ⚠️ ÉCRÊTÉ à « 99+ » : un compteur à quatre chiffres déforme ce qu'il annote, et la valeur exacte
 * n'apporte rien — ce qui compte est « beaucoup ».
 * ⚠️ `'!'` DOIT être verbalisé : un point d'exclamation n'est pas une information pour qui ne le voit
 * pas. D'où `srLabel` obligatoire.
 */
export function CountBadge({ value, tone = 'danger', overlay, srLabel }: {
  value: number | '!';
  tone?: BadgeTone;
  overlay?: boolean;
  srLabel: string;
}) {
  const texte = value === '!' ? '!' : (value > BADGE_COUNT.max ? `${BADGE_COUNT.max}+` : String(value));
  const fond = couleur(BADGE_TONES[tone].fg); // fond OPAQUE : seul cas où l'encre n'est pas sémantique
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: BADGE_COUNT.minWidth, height: BADGE_COUNT.height, padding: `0 ${BADGE_COUNT.padH}px`,
        // ⚠️ LE CHIFFRE PREND LA COULEUR DU FOND DE PAGE, jamais du blanc : le fond de la pastille est l'ENCRE du ton,
        //    sombre en thème clair mais claire en thème sombre — un blanc y devenait illisible. Le fond de page est
        //    toujours l'opposé de cette encre (maquette de la Console : `color:var(--canvas)`).
        borderRadius: 'var(--r-pill)', background: fond, color: 'var(--canvas)',
        fontSize: BADGE_CODE.fontSize, fontWeight: BADGE_WEIGHT, lineHeight: 1,
        ...(overlay
          ? { position: 'absolute', top: BADGE_COUNT.offset, right: BADGE_COUNT.offset }
          : null),
      }}
    >
      <span aria-hidden>{texte}</span>
      <span className="sr-only">{srLabel}</span>
    </span>
  );
}
